import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { FaUser, FaEnvelope, FaIdCard, FaGraduationCap, FaBuilding, FaCalendar, FaClock, FaMapMarker, FaArrowLeft } from 'react-icons/fa';

const EventRegistration = () => {
    const { eventId } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [event, setEvent] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        regNo: '',
        program: '',
        branch: '',
        year: '',
        section: '',
        contact: '',
        parentName: '',
        parentContact: '',
        parentEmail: ''
    });
    const [registrationComplete, setRegistrationComplete] = useState(false);
    const [registrationData, setRegistrationData] = useState(null);

    useEffect(() => {
        fetchEventDetails();
    }, [eventId]);

    const fetchEventDetails = async () => {
        try {
            setLoading(true);
            const response = await api.get(`/events/${eventId}`);
            setEvent(response.data.event);
        } catch (error) {
            console.error('Error fetching event:', error);
            toast.error('Failed to load event details');
            setTimeout(() => navigate('/'), 2000);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validate required fields
        const required = ['name', 'email', 'regNo', 'program', 'branch'];
        const missing = required.filter(field => !formData[field].trim());
        if (missing.length > 0) {
            toast.error(`Please fill in: ${missing.join(', ')}`);
            return;
        }

        setSubmitting(true);
        try {
            const registrationData = {
                ...formData,
                eventId: eventId
            };

            console.log('📤 Sending registration data:', registrationData);

            const response = await api.post('/register', registrationData);
            
            console.log('📥 Registration response:', response.data);
            
            // ✅ Check if response is successful - regardless of status code
            if (response.status === 200 || response.status === 201) {
                // ✅ Store registration data for success screen
                setRegistrationData({
                    name: formData.name,
                    email: formData.email,
                    regNo: formData.regNo
                });
                
                // ✅ Clear any previous toasts
                toast.dismiss();
                
                // ✅ Show success message
                toast.success('✅ Registration successful! Awaiting admin approval.', {
                    duration: 5000
                });
                toast.info('📧 Check your email for confirmation.', {
                    duration: 5000
                });
                
                setRegistrationComplete(true);
                
                // Reset form
                setFormData({
                    name: '',
                    email: '',
                    regNo: '',
                    program: '',
                    branch: '',
                    year: '',
                    section: '',
                    contact: '',
                    parentName: '',
                    parentContact: '',
                    parentEmail: ''
                });
            } else {
                // ✅ Handle case where response is not successful
                const errorMsg = response.data?.message || 'Registration failed. Please try again.';
                toast.error(errorMsg);
            }
        } catch (error) {
            console.error('❌ Registration error:', error);
            console.error('❌ Error response:', error.response?.data);
            
            // ✅ Clear any previous toasts
            toast.dismiss();
            
            // ✅ Check if the error is actually a success (sometimes 400 with success message)
            if (error.response?.data?.success === true) {
                // This is actually a success!
                setRegistrationData({
                    name: formData.name,
                    email: formData.email,
                    regNo: formData.regNo
                });
                toast.success('✅ Registration successful! Awaiting admin approval.', {
                    duration: 5000
                });
                toast.info('📧 Check your email for confirmation.', {
                    duration: 5000
                });
                setRegistrationComplete(true);
                setFormData({
                    name: '',
                    email: '',
                    regNo: '',
                    program: '',
                    branch: '',
                    year: '',
                    section: '',
                    contact: '',
                    parentName: '',
                    parentContact: '',
                    parentEmail: ''
                });
                return;
            }
            
            // ✅ Show specific error message
            if (error.response?.data?.message) {
                toast.error(error.response.data.message);
            } else if (error.response?.data?.error) {
                toast.error(error.response.data.error);
            } else {
                toast.error('Registration failed. Please try again.');
            }
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div style={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #0a1e3c 0%, #1a3a6a 100%)',
                padding: '20px'
            }}>
                <div style={{
                    background: 'white',
                    padding: '40px',
                    borderRadius: '12px',
                    textAlign: 'center'
                }}>
                    <div style={{ fontSize: '40px', marginBottom: '15px' }}>⏳</div>
                    <h3 style={{ color: '#0a1e3c' }}>Loading Event Details...</h3>
                </div>
            </div>
        );
    }

    if (!event) {
        return (
            <div style={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #0a1e3c 0%, #1a3a6a 100%)',
                padding: '20px'
            }}>
                <div style={{
                    background: 'white',
                    padding: '40px',
                    borderRadius: '12px',
                    textAlign: 'center',
                    maxWidth: '500px'
                }}>
                    <div style={{ fontSize: '48px', marginBottom: '15px' }}>❌</div>
                    <h3 style={{ color: '#0a1e3c' }}>Event Not Found</h3>
                    <p style={{ color: '#666', marginTop: '10px' }}>The event you're looking for doesn't exist or has been removed.</p>
                    <button
                        onClick={() => navigate('/')}
                        style={{
                            marginTop: '20px',
                            padding: '10px 30px',
                            background: '#0a1e3c',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '14px'
                        }}
                    >
                        Go to Home
                    </button>
                </div>
            </div>
        );
    }

    // ✅ Show success screen if registration is complete
    if (registrationComplete) {
        return (
            <div style={{
                minHeight: '100vh',
                background: 'linear-gradient(135deg, #0a1e3c 0%, #1a3a6a 100%)',
                padding: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}>
                <div style={{
                    background: 'white',
                    borderRadius: '16px',
                    padding: '40px',
                    maxWidth: '500px',
                    width: '100%',
                    textAlign: 'center',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
                }}>
                    <div style={{ fontSize: '64px', marginBottom: '20px' }}>🎉</div>
                    <h3 style={{ color: '#28a745', marginBottom: '10px' }}>Registration Successful!</h3>
                    <p style={{ color: '#666', marginBottom: '20px' }}>
                        Your registration has been submitted. Please wait for admin approval.
                    </p>
                    <div style={{
                        background: '#f8f9fa',
                        padding: '15px',
                        borderRadius: '8px',
                        marginBottom: '20px',
                        textAlign: 'left'
                    }}>
                        <p style={{ margin: '5px 0', fontSize: '14px' }}>
                            <strong>📧 Email:</strong> {registrationData?.email || formData.email}
                        </p>
                        <p style={{ margin: '5px 0', fontSize: '14px' }}>
                            <strong>📋 Registration No:</strong> {registrationData?.regNo || formData.regNo}
                        </p>
                        <p style={{ margin: '5px 0', fontSize: '14px' }}>
                            <strong>📌 Status:</strong> <span style={{ color: '#ffc107' }}>Pending Approval</span>
                        </p>
                    </div>
                    <button
                        onClick={() => navigate('/')}
                        style={{
                            padding: '12px 30px',
                            background: '#0a1e3c',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: 600
                        }}
                    >
                        Go to Home
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div style={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #0a1e3c 0%, #1a3a6a 100%)',
            padding: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        }}>
            <div style={{
                background: 'white',
                borderRadius: '16px',
                padding: '30px',
                maxWidth: '650px',
                width: '100%',
                maxHeight: '90vh',
                overflow: 'auto',
                boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
            }}>
                {/* Header */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '15px',
                    marginBottom: '20px',
                    paddingBottom: '15px',
                    borderBottom: '2px solid #f0f0f0'
                }}>
                    <button
                        onClick={() => navigate('/')}
                        style={{
                            background: 'none',
                            border: 'none',
                            fontSize: '20px',
                            cursor: 'pointer',
                            color: '#666',
                            padding: '5px'
                        }}
                    >
                        <FaArrowLeft />
                    </button>
                    <div>
                        <h3 style={{ color: '#0a1e3c', margin: 0, fontFamily: "'Times New Roman', Times, serif" }}>
                            Event Registration
                        </h3>
                        <p style={{ color: '#666', fontSize: '13px', margin: '2px 0 0 0' }}>
                            Fill in your details to register
                        </p>
                    </div>
                </div>

                {/* Event Details */}
                <div style={{
                    background: '#f8f9fa',
                    padding: '15px',
                    borderRadius: '8px',
                    marginBottom: '20px',
                    borderLeft: '4px solid #c9a84c'
                }}>
                    <h4 style={{ margin: '0 0 5px 0', color: '#0a1e3c' }}>{event.name}</h4>
                    <div style={{ fontSize: '13px', color: '#666' }}>
                        <span><FaCalendar style={{ marginRight: '5px' }} /> {new Date(event.date).toLocaleDateString()}</span>
                        <span style={{ marginLeft: '15px' }}><FaClock style={{ marginRight: '5px' }} /> {event.time}</span>
                        {event.venue && (
                            <span style={{ marginLeft: '15px' }}><FaMapMarker style={{ marginRight: '5px' }} /> {event.venue}</span>
                        )}
                    </div>
                </div>

                {/* Registration Form */}
                <form onSubmit={handleSubmit}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                        <div className="form-group">
                            <label style={{ fontSize: '13px', fontWeight: 600, display: 'block', marginBottom: '3px' }}>
                                <FaUser /> Full Name *
                            </label>
                            <input
                                type="text"
                                name="name"
                                className="form-control"
                                value={formData.name}
                                onChange={handleChange}
                                placeholder="Enter full name"
                                style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #ddd' }}
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label style={{ fontSize: '13px', fontWeight: 600, display: 'block', marginBottom: '3px' }}>
                                <FaEnvelope /> Email *
                            </label>
                            <input
                                type="email"
                                name="email"
                                className="form-control"
                                value={formData.email}
                                onChange={handleChange}
                                placeholder="Enter email"
                                style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #ddd' }}
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label style={{ fontSize: '13px', fontWeight: 600, display: 'block', marginBottom: '3px' }}>
                                <FaIdCard /> Registration No. *
                            </label>
                            <input
                                type="text"
                                name="regNo"
                                className="form-control"
                                value={formData.regNo}
                                onChange={handleChange}
                                placeholder="Enter registration number"
                                style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #ddd' }}
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label style={{ fontSize: '13px', fontWeight: 600, display: 'block', marginBottom: '3px' }}>
                                <FaGraduationCap /> Program *
                            </label>
                            <input
                                type="text"
                                name="program"
                                className="form-control"
                                value={formData.program}
                                onChange={handleChange}
                                placeholder="E.g. B.Tech"
                                style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #ddd' }}
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label style={{ fontSize: '13px', fontWeight: 600, display: 'block', marginBottom: '3px' }}>
                                <FaBuilding /> Branch *
                            </label>
                            <input
                                type="text"
                                name="branch"
                                className="form-control"
                                value={formData.branch}
                                onChange={handleChange}
                                placeholder="E.g. CSE"
                                style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #ddd' }}
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label style={{ fontSize: '13px', fontWeight: 600, display: 'block', marginBottom: '3px' }}>
                                Year
                            </label>
                            <input
                                type="text"
                                name="year"
                                className="form-control"
                                value={formData.year}
                                onChange={handleChange}
                                placeholder="E.g. 3"
                                style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #ddd' }}
                            />
                        </div>
                        <div className="form-group">
                            <label style={{ fontSize: '13px', fontWeight: 600, display: 'block', marginBottom: '3px' }}>
                                Section
                            </label>
                            <input
                                type="text"
                                name="section"
                                className="form-control"
                                value={formData.section}
                                onChange={handleChange}
                                placeholder="E.g. F"
                                style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #ddd' }}
                            />
                        </div>
                        <div className="form-group">
                            <label style={{ fontSize: '13px', fontWeight: 600, display: 'block', marginBottom: '3px' }}>
                                Contact Number
                            </label>
                            <input
                                type="text"
                                name="contact"
                                className="form-control"
                                value={formData.contact}
                                onChange={handleChange}
                                placeholder="Enter phone number"
                                style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #ddd' }}
                            />
                        </div>
                        <div className="form-group">
                            <label style={{ fontSize: '13px', fontWeight: 600, display: 'block', marginBottom: '3px' }}>
                                Parent Name
                            </label>
                            <input
                                type="text"
                                name="parentName"
                                className="form-control"
                                value={formData.parentName}
                                onChange={handleChange}
                                placeholder="Enter parent name"
                                style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #ddd' }}
                            />
                        </div>
                        <div className="form-group">
                            <label style={{ fontSize: '13px', fontWeight: 600, display: 'block', marginBottom: '3px' }}>
                                Parent Contact
                            </label>
                            <input
                                type="text"
                                name="parentContact"
                                className="form-control"
                                value={formData.parentContact}
                                onChange={handleChange}
                                placeholder="Enter parent contact"
                                style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #ddd' }}
                            />
                        </div>
                        <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                            <label style={{ fontSize: '13px', fontWeight: 600, display: 'block', marginBottom: '3px' }}>
                                <FaEnvelope /> Parent Email
                            </label>
                            <input
                                type="email"
                                name="parentEmail"
                                className="form-control"
                                value={formData.parentEmail}
                                onChange={handleChange}
                                placeholder="Enter parent email"
                                style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #ddd' }}
                            />
                        </div>
                    </div>

                    <div style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
                        <button
                            type="button"
                            onClick={() => navigate('/')}
                            style={{
                                padding: '10px 25px',
                                background: '#666',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                fontWeight: 600
                            }}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={submitting}
                            style={{
                                flex: 1,
                                padding: '10px 25px',
                                background: '#0a1e3c',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                fontWeight: 600,
                                opacity: submitting ? 0.7 : 1
                            }}
                        >
                            {submitting ? 'Registering...' : 'Register Now'}
                        </button>
                    </div>
                </form>

                <div style={{
                    marginTop: '15px',
                    padding: '12px',
                    background: '#fff3cd',
                    borderRadius: '8px',
                    fontSize: '12px',
                    color: '#856404'
                }}>
                    <strong>📌 Note:</strong>
                    <ul style={{ margin: '5px 0 0 15px', padding: 0 }}>
                        <li>Your account will be inactive until admin approval</li>
                        <li>You will receive a confirmation email after registration</li>
                        <li>Check your email for login credentials</li>
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default EventRegistration;